import React, { useState, useEffect } from 'react';
import { Select, MenuItem, Button, CircularProgress, Box, Typography, IconButton, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import { makeStyles } from '@material-ui/core/styles';
import axios from 'axios';

const useStyles = makeStyles((theme) => ({
  cellContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minHeight: 30,
  },
  select: {
    minWidth: 'clamp(100px, calc(44px + 4.167vw), 150px)',
    minHeight: 'clamp(24px, calc(8px + 1.25vw), 32px)',
    '& .MuiSelect-select': {
      fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)',
      paddingTop: 'clamp(2px, calc(-0.5px + 0.2906vw), 6px)',
      paddingBottom: 'clamp(2px, calc(-0.5px + 0.2906vw), 6px)',
      paddingLeft: 'clamp(7.06px, calc(0px + 1.4967vw), 9.41px)',
      paddingRight: 30,
      lineHeight: 1.4,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#999',
      borderRadius: 18,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
  },
  // Menu item font sizing to mirror cell content
  menuItem: {
    fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)'
  },
  addButton: {
    padding: 'clamp(3px, calc(-0.5px + 0.3906vw), 6px)',
    minHeight: 'clamp(24px, calc(8px + 1.25vw), 32px)',
    height: 'clamp(24px, calc(8px + 1.25vw), 32px)',
    minWidth: 'clamp(24px, calc(8px + 1.25vw), 32px)',
    width: 'clamp(24px, calc(8px + 1.25vw), 32px)',
    borderRadius: 18,
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    color: '#002D55',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: '#fff',
      borderColor: '#FF7F27',
    },
  },
  editButton: {
    padding: 'clamp(3px, calc(-0.5px + 0.3906vw), 6px)',
    minHeight: 'clamp(24px, calc(8px + 1.25vw), 32px)',
    height: 'clamp(24px, calc(8px + 1.25vw), 32px)',
    minWidth: 'clamp(24px, calc(8px + 1.25vw), 32px)',
    width: 'clamp(24px, calc(8px + 1.25vw), 32px)',
    borderRadius: 18,
    backgroundColor: 'transparent',
    border: '1px solid transparent',
    color: '#002D55',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: '#fff',
      borderColor: '#FF7F27',
    },
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 40,
    minWidth: 150,
  },
  errorText: {
    color: '#d32f2f',
    fontSize: '0.875rem',
  },
}));

/**
 * DiagnosisCell - Component for managing multiple treatments per patient
 * 
 * Allows physios to:
 * 1. View all treatments for a patient via dropdown
 * 2. Switch between treatments
 * 3. Add new assessment for a new diagnosis/condition
 * 
 * @param {object} props
 * @param {string} props.patientId - UUID of the patient
 * @param {array} props.treatments - List of treatment objects with id, diagnosis, diagnosis.diagnosis
 * @param {function} props.onTreatmentSelect - Callback when treatment is selected
 * @param {function} props.onNewTreatmentRequested - Callback to generate assessment link for new treatment
 */
const DiagnosisCell = ({ patientId, treatments, onTreatmentSelect, onNewTreatmentRequested, onEditTreatmentRequested, selectedTreatmentId: controlledSelectedId }) => {
  const classes = useStyles();
  const [uncontrolledId, setUncontrolledId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const resolvedSelectedTreatmentId = controlledSelectedId || uncontrolledId || (treatments && treatments.length ? treatments[0].id : '');
  const selectedTreatment = treatments && treatments.length
    ? (treatments.find(t => String(t.id) === String(resolvedSelectedTreatmentId)) || treatments[0])
    : null;

  // Set initial selected treatment to first available
  useEffect(() => {
    if (treatments && treatments.length > 0 && !controlledSelectedId && !uncontrolledId) {
      setUncontrolledId(treatments[0].id);
    }
  }, [treatments, controlledSelectedId, uncontrolledId]);

  const handleTreatmentChange = (event) => {
    const treatmentId = event.target.value;
    // Update internal state only when uncontrolled
    if (!controlledSelectedId) setUncontrolledId(treatmentId);
    setError('');
    
    // Notify parent of selection
    if (onTreatmentSelect) {
      const selectedTreatment = treatments.find(t => t.id === treatmentId);
      onTreatmentSelect(selectedTreatment);
    }
    
    // Stop propagation to prevent table row onClick from firing
    event.stopPropagation();
  };

  const handleNewTreatment = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Call parent callback to generate assessment link
      if (onNewTreatmentRequested) {
        await onNewTreatmentRequested();
      }
    } catch (err) {
      console.error('[DiagnosisCell] Error generating assessment link:', err);
      setError('Failed to generate assessment link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTreatment = async () => {
    if (!selectedTreatment) return;
    setIsLoading(true);
    setError('');

    try {
      if (onEditTreatmentRequested) {
        await onEditTreatmentRequested(selectedTreatment);
      }
    } catch (err) {
      console.error('[DiagnosisCell] Error opening treatment edit dialog:', err);
      setError('Failed to open treatment editor');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Show error if present
  if (error) {
    return (
      <Box className={classes.cellContainer}>
        <Typography className={classes.errorText}>{error}</Typography>
      </Box>
    );
  }

  // Show nothing if no treatments
  if (!treatments || treatments.length === 0) {
    return (
      <Box className={classes.cellContainer}>
        <Button
          onClick={handleNewTreatment}
          className={classes.addButton}
          title="Add treatment or view archived treatments"
          disabled={isLoading}
          size="small"
        >
          <AddIcon style={{ fontSize: 'clamp(14px, calc(5.5px + 0.677vw), 20px)' }} />
        </Button>
        <Typography variant="body2" color="textSecondary">
          No active treatments
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.cellContainer} onClick={(e) => e.stopPropagation()}>
      {/* Add new treatment button */}
      <Button
        onClick={handleNewTreatment}
        className={classes.addButton}
        title="Add treatment for this patient"
        disabled={isLoading}
        size="small"
      >
        <AddIcon style={{ fontSize: 'clamp(14px, calc(5.5px + 0.677vw), 20px)' }} />
      </Button>
      <Tooltip title={selectedTreatment ? 'Rename treatment' : 'No treatment selected'} arrow>
        <span>
          <IconButton
            onClick={handleEditTreatment}
            className={classes.editButton}
            size="small"
            disabled={isLoading || !selectedTreatment}
          >
            <EditIcon style={{ fontSize: 'clamp(14px, calc(5.5px + 0.677vw), 20px)' }} />
          </IconButton>
        </span>
      </Tooltip>
      {/* Treatment dropdown */}
      {treatments.length > 1 ? (
        <Select
          value={controlledSelectedId || uncontrolledId || ''}
          onChange={handleTreatmentChange}
          className={classes.select}
          size="small"
          variant="outlined"
          MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: { style: { borderRadius: 18, marginTop: 8 } } }}
          InputProps={{
          style: { backgroundColor: '#FFF', borderRadius: 18, overflow: 'hidden' },
        }}
        >
          {treatments.map((treatment, idx) => (
            <MenuItem key={`${treatment.id || 't'}-${treatment.diagnosis_id || treatment.identifier || idx}`} value={treatment.id} className={classes.menuItem}>
              {treatment.name || treatment.diagnosis?.diagnosis || 'Unknown Treatment'}
            </MenuItem>
          ))}
        </Select>
      ) : (
        // If only one treatment, show as text instead of dropdown
        <Typography variant="body2" className={classes.menuItem}>
          {treatments[0].name || treatments[0].diagnosis?.diagnosis || 'No treatment'}
        </Typography>
      )}

    </Box>
  );
};

export default DiagnosisCell;
